import { useState } from 'react'
import { Input, message } from 'antd'
import { useNavigate } from 'react-router-dom'
import { UploadedFile } from '@/types/dashboard'
import { documentTypeOptions } from '@/data/documentTypes'
import FileList from './FileList'
import UploadControls from './UploadControls'
import { useMutation } from '@tanstack/react-query'
import { apiService } from '@/lib/api'

export default function UploadInputArea() {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([])
  const [inputValue, setInputValue] = useState('')
  const navigate = useNavigate()

  const handleFileUpload = (files: FileList | null) => {
    if (!files) return

    const newFiles = Array.from(files).map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      type: file.type,
      file: file
    }))

    setUploadedFiles(prev => [...prev, ...newFiles])
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    handleFileUpload(e.dataTransfer.files)
  }

  const removeFile = (id: number) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== id))
  }

  const handleDocumentTypeSelect = (key: string) => {
    const selectedType = documentTypeOptions.find(opt => opt.key === key)
    if (selectedType) {
      setInputValue(prev => `${prev} [Document Type: ${selectedType.label}]`)
    }
  }

  const createChatMutation = useMutation({
    mutationFn: (data: { instruction?: string; files?: File[]; title?: string }) =>
      apiService.createChatSession(data),
    onSuccess: (response) => {
      if (response.success && response.data) {
        // Redirect to chat page with session ID
        navigate(`/chat/${response.data.id}`)
      } else {
        message.error(response.message || 'Failed to create chat session')
      }
    },
    onError: (error) => {
      console.error('Error creating chat session:', error)
      message.error('Failed to create chat session. Please try again.')
    }
  })

  const handleSubmit = () => {
    const files = uploadedFiles.map(file => file.file)
    const instruction = inputValue.trim()

    // Generate a title from the instruction or file names
    let title = ''
    if (instruction) {
      title = instruction.length > 50 ? instruction.substring(0, 50) + '...' : instruction
    } else if (uploadedFiles.length > 0) {
      title = `Document Processing - ${uploadedFiles.length} file${uploadedFiles.length > 1 ? 's' : ''}`
    }

    createChatMutation.mutate({
      instruction: instruction || undefined,
      files: files.length > 0 ? files : undefined,
      title: title || undefined
    })
  }

  const hasContent = uploadedFiles.length > 0 || inputValue.trim().length > 0

  return (
    <div
      className="relative group max-w-4xl mx-auto mb-8"
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Gradient border effect */}
      <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-2xl opacity-30 group-hover:opacity-60 blur transition duration-500"></div>

      {/* Main container */}
      <div className="relative bg-[#0F1422] rounded-2xl p-6 border border-slate-700/50 backdrop-blur-xl shadow-lg">
        <Input.TextArea
          placeholder="e.g. upload my expense receipt from restaurant dinner with client, categorize as business meal expense"
          variant="borderless"
          autoSize={{ minRows: 2, maxRows: 4 }}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          style={{
            fontSize: 15,
            marginBottom: 16,
            resize: 'none',
            backgroundColor: 'transparent',
            color: '#e2e8f0',
            padding: 0
          }}
          className="text-slate-200 placeholder-slate-500"
        />

        <FileList files={uploadedFiles} onRemove={removeFile} isProcessing={createChatMutation.isPending} />
        <UploadControls
          onFileUpload={handleFileUpload}
          onDocumentTypeSelect={handleDocumentTypeSelect}
          onSubmit={handleSubmit}
          hasContent={hasContent}
          isLoading={createChatMutation.isPending}
        />
      </div>
    </div>
  )
}
